import { useEffect, useState } from "react";
import { CalendarDays, Users, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { BookCallButton } from "@/components/site/BookCall";

const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const BADGE = { open: ["Now booking", "bg-emerald-100 text-emerald-800"], waitlist: ["Opening soon — join the waitlist", "bg-amber-100 text-amber-800"], full: ["Full — join the waitlist", "bg-slate-200 text-slate-600"] };

export function useRetreats() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/retreats").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return rows;
}

function RetreatCard({ r }) {
  const [label, cls] = BADGE[r.status];
  return (
    <div className="rounded-2xl bg-white border border-[#0B2A4A]/10 p-6 flex flex-col gap-3" data-testid={`retreat-card-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-[#0B2A4A]">{r.label}</h3>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`} data-testid="retreat-status">{label}</span>
      </div>
      <p className="flex gap-2 text-sm text-slate-700"><CalendarDays className="h-4 w-4 mt-0.5 text-[#F08A3C] shrink-0" />{fmt(r.check_in)} → {fmt(r.check_out)} · 8 nights</p>
      <p className="flex gap-2 text-sm font-semibold text-[#0B2A4A]" data-testid="retreat-seats"><Users className="h-4 w-4 mt-0.5 text-[#F08A3C] shrink-0" />{r.seats_left} of {r.max_seats} seats left</p>
      {r.note && <p className="flex gap-2 text-sm text-slate-600"><Sparkles className="h-4 w-4 mt-0.5 text-[#1A9AA8] shrink-0" />{r.note}</p>}
      <div className="mt-auto pt-2"><BookCallButton source={`retreat-dates-${r.id}`} interest={r.id} className="w-full justify-center py-2.5 text-sm" testId={`retreat-book-btn-${r.id}`} label={r.status === "open" ? "Book a free call to reserve" : "Join the waitlist (free call)"} /></div>
    </div>
  );
}

export function RetreatDates() {
  const rows = useRetreats();
  if (!rows.length) return null;
  return (
    <section id="retreat-dates" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 scroll-mt-24" data-testid="retreat-dates">
      <p className="font-mono-ui text-xs tracking-[0.3em] text-[#1A9AA8]">7-DAY RETREAT DATES · Esc DIGITAL NOMAD VILLAGE</p>
      <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A4A]">Small groups, so every student gets real attention</h2>
      <p className="mt-3 max-w-3xl text-slate-600">Every retreat is 8 nights: check in Sunday from 2:00 pm, the program runs Monday to Sunday (Days 1–7), and you check out Monday morning. Groups are capped at 10 people (the pilot at 5) because Greg works with each student individually to understand their needs and create opportunities together.</p>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{rows.map((r) => <RetreatCard key={r.id} r={r} />)}</div>
      <p className="mt-4 text-xs text-slate-500">No retreats over Christmas or New Year.</p>
    </section>
  );
}
